import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/common/StatusBadge';
import {
  AlertTriangle,
  History,
  FileText,
  RotateCcw,
  CheckCircle2,
  Calendar,
  Layers,
  ArrowRight,
  ShieldAlert,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { ReconciliationIssueRecord } from '@/types/database';
import { reconciliationService } from '@/services/reconciliationService';
import { formatDate } from '@/lib/utils';

interface IssueDetailDrawerProps {
  issue: ReconciliationIssueRecord | null;
  isOpen: boolean;
  onClose: () => void;
  onOpenResolveModal: (issue: ReconciliationIssueRecord) => void;
  onReopenSuccess: () => void;
}

export const IssueDetailDrawer: React.FC<IssueDetailDrawerProps> = ({
  issue,
  isOpen,
  onClose,
  onOpenResolveModal,
  onReopenSuccess,
}) => {
  const [reopening, setReopening] = useState<boolean>(false);
  const [showReopenConfirm, setShowReopenConfirm] = useState<boolean>(false);
  const [reopenNotes, setReopenNotes] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  if (!issue) return null;

  const sourceRows: number[] = Array.isArray(issue.source_rows)
    ? issue.source_rows
    : typeof issue.source_rows === 'string'
    ? JSON.parse(issue.source_rows || '[]')
    : [];

  const previousResolutions: any[] = Array.isArray(issue.previous_resolutions)
    ? issue.previous_resolutions
    : typeof issue.previous_resolutions === 'string'
    ? JSON.parse(issue.previous_resolutions || '[]')
    : [];

  const handleReopen = async () => {
    setError(null);
    setReopening(true);

    try {
      await reconciliationService.reopenIssue(issue.id, reopenNotes);
      setShowReopenConfirm(false);
      onReopenSuccess();
      onClose();
    } catch (err: any) {
      console.error('Failed to reopen issue:', err);
      setError(err.message || 'Failed to reopen issue');
    } finally {
      setReopening(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold text-slate-900">
                  Issue Diagnostic & Audit Trail
                </DialogTitle>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[11px] font-mono text-slate-500">ID: {issue.id.slice(0, 8)}...</span>
                  <StatusBadge status={issue.status} category="issue" />
                  <StatusBadge status={issue.severity} category="severity" />
                </div>
              </div>
            </div>
          </div>
        </DialogHeader>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="space-y-4 text-xs">
          {/* Section 1: Excel Lineage & Ground Truth */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3.5 space-y-2">
            <div className="flex items-center justify-between border-b border-slate-200/80 pb-1.5">
              <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">
                Excel Evidence & Source Lineage
              </span>
              <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-slate-200 text-slate-700 font-bold">
                {issue.source_sheet}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <span className="text-slate-500">Raw Excel Value:</span>
                <div className="font-mono font-bold text-slate-900 mt-0.5 text-xs break-all">
                  {issue.legacy_value || 'N/A'}
                </div>
              </div>
              <div>
                <span className="text-slate-500">Target Entity Type:</span>
                <div className="font-mono text-slate-800 font-semibold mt-0.5">{issue.entity_type}</div>
              </div>
            </div>

            {sourceRows.length > 0 && (
              <div>
                <span className="text-slate-500 block mb-1">Source Row Lineage ({sourceRows.length} rows):</span>
                <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
                  {sourceRows.map((r, i) => (
                    <span key={i} className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-slate-200 text-slate-800 font-bold">
                      Row {r}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Section 2: Factual Diagnostic Description */}
          <div className="p-3.5 rounded-lg border border-slate-200 bg-white space-y-2">
            <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">
              Issue Diagnostic
            </span>
            <div className="text-slate-900 font-medium text-xs leading-relaxed">
              {issue.description}
            </div>
            {issue.suggested_action && (
              <div className="p-2.5 rounded bg-amber-50 border border-amber-200 text-amber-900 text-[11px] leading-relaxed">
                💡 <strong className="font-semibold">Suggested Possibility:</strong> {issue.suggested_action}
              </div>
            )}
          </div>

          {/* Section 3: Associated Production Cycle (if linked) */}
          {issue.production_cycles && (
            <div className="p-3.5 rounded-lg border border-indigo-100 bg-indigo-50/40 space-y-2">
              <span className="font-semibold text-indigo-900 uppercase tracking-wider text-[10px]">
                Linked Production Cycle
              </span>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                  <span className="text-slate-500 text-[10px]">Cycle Code:</span>
                  <div className="font-mono font-bold text-slate-900 text-xs">
                    {issue.production_cycles.cycle_code}
                  </div>
                </div>
                <div>
                  <span className="text-slate-500 text-[10px]">Batch:</span>
                  <div className="font-mono text-slate-800 text-xs">
                    {issue.production_cycles.batch_code}
                  </div>
                </div>
                <div>
                  <span className="text-slate-500 text-[10px]">Grower:</span>
                  <div className="text-slate-900 text-xs font-medium">
                    {issue.production_cycles.growers?.full_name || issue.production_cycles.legacy_raiser_name || 'N/A'}
                  </div>
                </div>
                <div>
                  <span className="text-slate-500 text-[10px]">Status:</span>
                  <div><StatusBadge status={issue.production_cycles.status} category="cycle" /></div>
                </div>
              </div>
            </div>
          )}

          {/* Section 4: Current Resolution State */}
          {issue.status !== 'needs_review' && (
            <div className="p-3.5 rounded-lg border border-emerald-200 bg-emerald-50/50 space-y-2">
              <div className="flex items-center justify-between border-b border-emerald-200 pb-1.5">
                <span className="font-bold text-emerald-900 uppercase tracking-wider text-[10px] flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  Active Resolution Details
                </span>
                <span className="text-[10px] text-emerald-800 font-mono">
                  {issue.resolved_at ? formatDate(issue.resolved_at) : 'N/A'}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-slate-500">Selected Decision:</span>
                  <div className="font-mono font-bold text-slate-900 mt-0.5">
                    {issue.selected_decision || 'N/A'}
                  </div>
                </div>
                <div>
                  <span className="text-slate-500">Resolution Notes:</span>
                  <div className="text-slate-800 mt-0.5 italic">
                    {issue.resolution_notes || 'No admin notes recorded'}
                  </div>
                </div>
              </div>
              {issue.resolution_payload && (
                <div className="pt-2 border-t border-emerald-100">
                  <span className="text-slate-500 text-[10px]">Structured Payload:</span>
                  <pre className="mt-1 p-2 rounded bg-white border border-emerald-200 text-[10px] font-mono text-slate-800 overflow-x-auto">
                    {JSON.stringify(issue.resolution_payload, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* Section 5: Reversible Decision History (previous_resolutions JSONB) */}
          {previousResolutions.length > 0 && (
            <div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50 space-y-2.5">
              <div className="flex items-center gap-1.5 font-bold text-slate-800 uppercase tracking-wider text-[10px]">
                <History className="w-3.5 h-3.5 text-slate-600" />
                Previous Decision History ({previousResolutions.length})
              </div>

              <div className="space-y-2 max-h-40 overflow-y-auto">
                {previousResolutions.map((prev, idx) => (
                  <div key={idx} className="p-2.5 rounded bg-white border border-slate-200 text-[11px] space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-bold text-slate-800">
                        {prev.selected_decision}
                      </span>
                      <span className="text-[10px] text-slate-500 font-mono">
                        Reopened: {formatDate(prev.reopened_at)}
                      </span>
                    </div>
                    {prev.reopen_notes && (
                      <p className="text-amber-800 text-[10px] italic">
                        Reopen reason: {prev.reopen_notes}
                      </p>
                    )}
                    {prev.resolution_notes && (
                      <p className="text-slate-600 text-[10px]">
                        Prior notes: {prev.resolution_notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reopen Prompt Modal Inline */}
          {showReopenConfirm && (
            <div className="p-3.5 rounded-lg border border-amber-300 bg-amber-50 space-y-2">
              <div className="flex items-center gap-2 font-bold text-amber-900 text-xs">
                <RotateCcw className="w-4 h-4 text-amber-600" />
                <span>Confirm Reopening This Issue</span>
              </div>
              <p className="text-[11px] text-slate-600">
                Reopening will restore this item to the <strong>Needs Review</strong> active queue and archive the current resolution into the permanent audit trail.
              </p>
              <div>
                <Label className="text-[11px] font-semibold text-slate-700">Reason for Reopening *</Label>
                <Input
                  type="text"
                  value={reopenNotes}
                  onChange={(e) => setReopenNotes(e.target.value)}
                  placeholder="e.g. Received updated paperwork from grower"
                  className="mt-1 text-xs h-8 bg-white"
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowReopenConfirm(false)}
                  disabled={reopening}
                  className="text-xs h-7"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleReopen}
                  disabled={reopening}
                  className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-7 gap-1"
                >
                  {reopening ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>Reopening...</span>
                    </>
                  ) : (
                    <>
                      <RotateCcw className="w-3 h-3" />
                      <span>Confirm Reopen Decision</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 justify-between pt-2 border-t border-slate-100">
          <div>
            {issue.status !== 'needs_review' && !showReopenConfirm && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowReopenConfirm(true)}
                className="min-h-[44px] text-xs text-amber-700 border-amber-300 hover:bg-amber-50 gap-1.5 font-semibold"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Reopen Decision</span>
              </Button>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="min-h-[44px] text-xs font-semibold text-slate-700"
            >
              Close
            </Button>
            {issue.status === 'needs_review' && (
              <Button
                onClick={() => {
                  onClose();
                  onOpenResolveModal(issue);
                }}
                className="min-h-[44px] bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold gap-1.5"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>Resolve Issue</span>
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
